import { createFileRoute, Link } from "@tanstack/react-router";

import { SiteLayout } from "@/components/SiteLayout";

const TITLE = "Sobre o TrajetoFácil — metodologia e fontes oficiais";
const DESCRIPTION =
  "Conheça o TrajetoFácil: como calculamos rota, combustível e pedágio, quais fontes oficiais usamos (ANTT, ARTESP e concessionárias) e com que frequência atualizamos as tarifas.";
const URL = "https://www.trajetofacil.com.br/sobre";

export const Route = createFileRoute("/sobre")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: URL }],
  }),
  component: AboutPage,
});

function AboutPage() {
  return (
    <SiteLayout>
      <article className="space-y-8 text-base leading-7 text-foreground">
        <header>
          <h1 className="text-3xl font-extrabold tracking-tight">Sobre o TrajetoFácil</h1>
          <p className="mt-3 text-muted-foreground">
            O TrajetoFácil é um roteirizador gratuito criado para responder uma pergunta simples antes de
            qualquer viagem: quanto vou gastar no total — distância, tempo, combustível e pedágio — na ida
            e na volta?
          </p>
        </header>

        <section className="space-y-3">
          <h2 className="text-xl font-bold">O que a ferramenta faz</h2>
          <p>
            A partir de uma origem e um destino (CEP, cidade ou endereço), calculamos a rota em mapa,
            a distância e o tempo de cada trecho, o gasto estimado de combustível a partir do preço do
            litro e do consumo do veículo, e o pedágio praça por praça. É possível calcular ida e volta,
            adicionar até seis paradas, compartilhar a rota por link e aplicar um multiplicador de repasse
            para precificar a viagem.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-bold">Como calculamos o pedágio</h2>
          <p>
            Cruzamos a geometria da rota com uma base própria de praças de pedágio e somamos as tarifas
            conforme a categoria do veículo — carro, moto, ônibus ou caminhão, com cobrança por número de
            eixos quando aplicável. Praças gêmeas (uma em cada sentido) são tratadas separadamente para
            evitar cobrança duplicada, e ida e volta são calculados como trajetos independentes.
          </p>
          <p>
            As tarifas vêm de fontes oficiais: ANTT (rodovias federais), ARTESP e demais concessionárias
            estaduais. Uma rotina automática revisa a base semanalmente, para que os valores acompanhem os
            reajustes publicados.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-bold">Compromisso com a transparência</h2>
          <p>
            Todo valor exibido é uma estimativa de referência. O valor efetivamente pago pode variar por
            sentido, uso de tag, descontos da concessionária ou reajustes muito recentes. Sempre que a
            precisão for crítica, confira a tarifa vigente no site da concessionária do trecho.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-bold">Gratuito e sem cadastro</h2>
          <p>
            A calculadora é gratuita e não exige cadastro. O projeto é mantido com exibição de anúncios, e
            o feedback dos usuários orienta as melhorias. Fale com a gente pela{" "}
            <Link to="/contato" className="font-medium text-primary underline underline-offset-4">
              página de contato
            </Link>{" "}
            ou explore os{" "}
            <Link to="/guias" className="font-medium text-primary underline underline-offset-4">
              guias de planejamento de viagem
            </Link>
            .
          </p>
        </section>
      </article>
    </SiteLayout>
  );
}
